import { useEffect, useState } from "react";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { Switch } from "../../ui/switch";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "../../ui/dropdown-menu";
import {
  Brain, Loader2, RefreshCw, ChevronRight, Sparkles,
  MoreVertical, Mail, TrendingDown,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { HealthScoreBadge } from "./HealthScoreBadge";
import { BenchmarkBar } from "./BenchmarkBar";

/**
 * Monthly AI insight card — renders the Claude analysis, KPIs,
 * benchmark bar, health score badge, and the action menu
 * (email preferences / send now / open variance alerts).
 */
export function InsightCard({ restaurantId, insight, loading, error, onRefresh, onOpenVariance }) {
  const data = insight?.insight;
  const hasContent = data && (data.headline || (data.bullets?.length) || data.suggestion);
  const generatedAt = insight?.generated_at
    ? new Date(insight.generated_at).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" })
    : null;
  const health = insight?.metrics?.health_score;
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!restaurantId) return;
    (async () => {
      try {
        const p = await api.request(`/restaurants/${restaurantId}/stock-movements/insight/preferences`);
        setEmailEnabled(p.insight_email_enabled !== false);
      } catch { /* silent */ }
      try {
        const h = await api.request(`/restaurants/${restaurantId}/stock-movements/insight/history?months=12`);
        setHistory(h.months || []);
      } catch { /* silent */ }
    })();
  }, [restaurantId]);

  const toggleEmail = async (next) => {
    setEmailEnabled(next);
    try {
      await api.request(`/restaurants/${restaurantId}/stock-movements/insight/preferences`, {
        method: "PATCH",
        body: JSON.stringify({ insight_email_enabled: next }),
      });
      toast.success(next ? "Email mensual activado" : "Email mensual desactivado");
    } catch (e) {
      setEmailEnabled(!next);
      toast.error(e?.message || "No se pudo guardar la preferencia");
    }
  };

  const sendEmailNow = async () => {
    setSendingEmail(true);
    try {
      const res = await api.request(`/restaurants/${restaurantId}/stock-movements/insight/send-email`, {
        method: "POST",
      });
      if (res?.sent) {
        toast.success(`Email enviado a ${res.email}`);
      } else {
        toast.message("No se envió", { description: res?.reason || "Sin datos suficientes" });
      }
    } catch (e) {
      toast.error(e?.message || "No se pudo enviar el email");
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Card
      className="border-0 bg-gradient-to-br from-violet-500/10 via-fuchsia-500/5 to-cyan-500/10 dark:from-violet-500/15 dark:via-fuchsia-500/10 dark:to-cyan-500/15 shadow-sm overflow-hidden relative"
      data-testid="inventory-insight-card"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(139,92,246,0.12),transparent_60%)] pointer-events-none" />
      <CardContent className="p-5 relative">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-500/25">
              <Brain className="w-4.5 h-4.5 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="font-semibold text-sm tracking-tight">Análisis mensual IA</h2>
              <p className="text-[11px] text-muted-foreground">
                Últimos 30 días · {generatedAt ? `Actualizado ${generatedAt}` : "Generando…"}
                {insight?.cached && <span className="ml-1 opacity-70">· en caché</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <HealthScoreBadge health={health} history={history} />
            <Button
              variant="ghost"
              size="sm"
              onClick={onRefresh}
              disabled={loading}
              className="gap-1.5 text-xs h-7"
              data-testid="inv-insight-refresh-btn"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {loading ? "Analizando…" : "Actualizar"}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7" data-testid="inv-insight-menu-btn" aria-label="Opciones del análisis de inventario">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="text-xs">Email mensual</DropdownMenuLabel>
                <div className="px-2 py-1.5 flex items-center justify-between gap-2" data-testid="inv-insight-email-toggle-row">
                  <div className="min-w-0">
                    <p className="text-xs font-medium">Recibir resumen por email</p>
                    <p className="text-[10px] text-muted-foreground">Primer lunes de cada mes</p>
                  </div>
                  <Switch
                    checked={emailEnabled}
                    onCheckedChange={toggleEmail}
                    data-testid="inv-insight-email-toggle"
                  />
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={sendEmailNow}
                  disabled={sendingEmail}
                  className="gap-2 cursor-pointer"
                  data-testid="inv-insight-send-email-now"
                >
                  {sendingEmail ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                  <span className="text-xs">{sendingEmail ? "Enviando…" : "Enviarme ahora (preview)"}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onOpenVariance?.()}
                  className="gap-2 cursor-pointer"
                  data-testid="inv-variance-alert-menu-item"
                >
                  <TrendingDown className="w-3.5 h-3.5 text-red-500" />
                  <span className="text-xs">Alertas de merma oculta…</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {loading && !hasContent ? (
          <div className="space-y-2 animate-pulse">
            <div className="h-5 w-3/4 bg-foreground/10 rounded" />
            <div className="h-3 w-5/6 bg-foreground/5 rounded" />
            <div className="h-3 w-2/3 bg-foreground/5 rounded" />
            <div className="h-3 w-4/5 bg-foreground/5 rounded" />
          </div>
        ) : error ? (
          <p className="text-sm text-muted-foreground">
            No se pudo generar el análisis en este momento. Inténtalo de nuevo en unos segundos.
          </p>
        ) : hasContent ? (
          <div className="space-y-3">
            {data.headline && (
              <p className="text-[15px] font-semibold leading-snug text-foreground" data-testid="inv-insight-headline">
                {data.headline}
              </p>
            )}
            <BenchmarkBar benchmark={insight?.metrics?.benchmark} />
            {data.bullets?.length > 0 && (
              <ul className="space-y-1.5" data-testid="inv-insight-bullets">
                {data.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground/90 leading-relaxed">
                    <ChevronRight className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-violet-500" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
            {data.suggestion && (
              <div className="rounded-lg border border-violet-500/20 bg-card/60 dark:bg-white/5 backdrop-blur-sm px-3 py-2.5 text-sm" data-testid="inv-insight-suggestion">
                <div className="flex items-start gap-2">
                  <Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-fuchsia-500" />
                  <span className="text-foreground/90 leading-relaxed"><strong className="font-semibold">Acción sugerida: </strong>{data.suggestion}</span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Aún no tenemos suficientes datos del último mes para generar un análisis. Registra entradas, ventas y mermas para activarlo.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
